import { useMemo, useEffect } from 'react';
import { createTheme } from '@mui/material/styles';
import { useThemeMode } from '../contexts/ThemeContext';
import { useAuth } from './useAuth';
import { useAppSettings } from '../contexts/AppSettingsContext';
import { getTheme as getThemeTokens, injectAnimations, getThemeCSSVars, contrast } from '../theme/themes';
import { typography, radii } from '../theme/tokens/base';

export const useTheme = () => {
  const { mode } = useThemeMode();
  const { user } = useAuth();
  const { defaultTheme } = useAppSettings();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const themeTokens = useMemo(() => getThemeTokens(user), [user?.theme, defaultTheme]);

  // Load theme-specific fonts on demand
  useEffect(() => {
    const fonts = themeTokens.typography;
    if (!fonts) return;
    const needed = new Set();
    const values = [fonts.headline, fonts.body, fonts.mono].filter(Boolean).join(' ');
    if (values.includes('Press Start 2P')) needed.add('Press+Start+2P');
    if (values.includes('JetBrains Mono')) needed.add('JetBrains+Mono:wght@500;700');
    if (values.includes('Sora')) needed.add('Sora:wght@400;600;700;800');
    if (needed.size === 0) return;
    const id = 'theme-fonts';
    let link = document.getElementById(id);
    const href = `https://fonts.googleapis.com/css2?${[...needed].map(f => `family=${f}`).join('&')}&display=swap`;
    if (link && link.href === href) return;
    if (link) link.remove();
    link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }, [themeTokens]);

  // Inject animations and CSS variables for super themes
  useEffect(() => {
    if (themeTokens.isSuper) {
      injectAnimations(themeTokens);
      const cssVars = getThemeCSSVars(themeTokens);
      Object.entries(cssVars).forEach(([key, value]) => {
        document.documentElement.style.setProperty(key, value);
      });
    }
    return () => {
      // Cleanup CSS vars on theme change
      document.documentElement.style.removeProperty('--theme-main');
      document.documentElement.style.removeProperty('--theme-light');
      document.documentElement.style.removeProperty('--theme-dark');
      document.documentElement.style.removeProperty('--theme-bg');
      document.documentElement.style.removeProperty('--theme-glow');
    };
  }, [themeTokens]);

  const theme = useMemo(() => {
    const isDark = mode === 'dark';
    const palette = themeTokens;

    // Some themes (like Galaxy) have dark backgrounds even in "light" mode
    const forceDarkText = palette.isDarkTheme === true;
    const effectiveDark = isDark || forceDarkText;

    // Use solid color for background.default (gradients don't work here)
    const bgDefault = effectiveDark
      ? (palette.darkBg || '#1a1a2e')
      : (palette.surfaces?.lightBg || palette.bg);

    return createTheme({
      palette: {
        mode: effectiveDark ? 'dark' : 'light',
        primary: {
          main: palette.main,
          light: palette.light,
          dark: palette.dark,
          contrastText: effectiveDark
            ? (palette.surfaces?.primaryText || palette.contrastText || '#fff')
            : (palette.surfaces?.primaryTextLight || palette.contrastText || '#fff'),
        },
        secondary: {
          main: palette.light,
          light: palette.bg,
          dark: palette.main,
          contrastText: '#fff',
        },
        success: {
          main: palette.surfaces?.success || '#2e7d32',
          light: palette.surfaces?.successLight || '#4caf50',
          dark: palette.surfaces?.successDark || '#1b5e20',
        },
        warning: {
          main: palette.surfaces?.warning || '#ed6c02',
          light: palette.surfaces?.warningLight || '#ff9800',
          dark: palette.surfaces?.warningDark || '#e65100',
        },
        error: {
          main: palette.surfaces?.error || '#d32f2f',
          light: palette.surfaces?.errorLight || '#ef5350',
          dark: palette.surfaces?.errorDark || '#c62828',
        },
        background: {
          default: bgDefault,
          paper: effectiveDark
            ? (palette.surfaces?.containerHigh || contrast.lighten(palette.darkBg || '#1a1a2e', 0.08))
            : (palette.surfaces?.lightPaper || '#ffffff'),
          sidebar: effectiveDark
            ? (palette.surfaces?.containerLowest || contrast.darken(palette.darkBg || '#1a1a2e', 0.15))
            : (palette.surfaces?.lightPaper || '#ffffff'),
          container: effectiveDark
            ? (palette.surfaces?.container || contrast.lighten(palette.darkBg || '#1a1a2e', 0.04))
            : (palette.surfaces?.lightContainer || '#f5f5f5'),
          containerHigh: effectiveDark
            ? (palette.surfaces?.containerHighest || contrast.lighten(palette.darkBg || '#1a1a2e', 0.12))
            : (palette.surfaces?.lightPaper || '#fafafa'),
        },
        text: {
          primary: effectiveDark ? (palette.surfaces?.onSurface || '#f0f0f0') : (palette.surfaces?.lightOnSurface || '#1a1a2e'),
          secondary: effectiveDark ? (palette.surfaces?.onSurfaceVariant || '#aaaaaa') : (palette.surfaces?.lightOnSurfaceVariant || '#666666'),
        },
        divider: effectiveDark ? (palette.surfaces?.outlineVariant || 'rgba(255,255,255,0.12)') : (palette.surfaces?.lightOutline || 'rgba(0,0,0,0.12)'),
      },
      typography: {
        fontFamily: typography.fontFamily.primary,
        h1: { fontSize: '2.5rem', fontWeight: typography.fontWeight.bold },
        h2: { fontSize: '2rem',   fontWeight: typography.fontWeight.bold },
        h3: { fontSize: '1.75rem', fontWeight: typography.fontWeight.bold },
        h4: { fontSize: '1.5rem', fontWeight: typography.fontWeight.bold },
        h5: { fontSize: '1.25rem', fontWeight: typography.fontWeight.bold },
        h6: { fontSize: '1rem',   fontWeight: typography.fontWeight.bold },
        button: { textTransform: 'none', fontWeight: typography.fontWeight.bold },
      },
      shape: { borderRadius: radii.md },
      // Custom theme extensions for super themes
      themeExtras: {
        isSuper: palette.isSuper || false,
        animations: palette.animations || null,
        effects: palette.effects || null,
        glowColor: palette.effects?.glowColor || `${palette.main}40`,
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            '*, *::before, *::after': {
              WebkitTapHighlightColor: 'transparent',
            },
          },
        },
        MuiButton: {
          defaultProps: {
            variant: 'contained',
            disableElevation: true,
          },
          styleOverrides: {
            root: {
              borderRadius: radii.pill,
              padding: '10px 24px',
              fontSize: '0.9rem',
              lineHeight: 1.5,
              fontWeight: typography.fontWeight.bold,
              ...(palette.isSuper && palette.animations?.buttonHover && {
                '&:hover': {
                  animation: `${palette.animations.buttonHover.name} ${palette.animations.buttonHover.duration} ${palette.animations.buttonHover.timing}`,
                },
              }),
            },
            sizeSmall: {
              padding: '5px 14px',
              fontSize: '0.8rem',
              lineHeight: 1.5,
            },
            contained: {
              boxShadow: `0 2px 0px ${palette.main}40`,
              '&:hover': {
                boxShadow: palette.isSuper
                  ? `0 4px 0px ${palette.effects?.glowColor || palette.main + '59'}`
                  : `0 4px 0px ${palette.main}59`,
              },
            },
            outlined: ({ ownerState, theme }) => ({
              border: 'none',
              boxShadow: 'none',
              backgroundColor: theme.palette[ownerState.color]?.main || theme.palette.primary.main,
              color: theme.palette[ownerState.color]?.contrastText || '#fff',
              '&:hover': {
                backgroundColor: theme.palette[ownerState.color]?.main || theme.palette.primary.main,
                filter: 'brightness(.9)',
              },
            }),
            text: {
              color: effectiveDark
                ? (palette.surfaces?.labelColor || palette.main)
                : (palette.surfaces?.labelColorLight || palette.dark),
              backgroundColor: 'transparent',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: `${palette.main}14`,
              },
            },
          },
          variants: [
            {
              props: { color: 'inverse' },
              style: {
                backgroundColor: effectiveDark
                  ? (palette.surfaces?.inverseSurface || '#fff')
                  : (palette.surfaces?.inverseSurfaceLight || '#1a1a2e'),
                color: effectiveDark
                  ? (palette.surfaces?.onInverseSurface || '#1a1a2e')
                  : (palette.surfaces?.onInverseSurfaceLight || '#fff'),
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: effectiveDark
                    ? (palette.surfaces?.inverseSurface || '#fff')
                    : (palette.surfaces?.inverseSurfaceLight || '#1a1a2e'),
                  filter: 'brightness(0.92)',
                },
              },
            },
          ],
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 20,
              boxShadow: isDark ? '0 2px 12px rgba(0,0,0,0.4)' : `0 2px 12px ${palette.main}14`,
              // Super theme card shimmer
              ...(palette.isSuper && palette.animations?.cardHover && {
                '&:hover': {
                  animation: `${palette.animations.cardHover.name} ${palette.animations.cardHover.duration} ${palette.animations.cardHover.timing}`,
                },
              }),
            },
          },
        },
        MuiPaper: { styleOverrides: { root: { borderRadius: radii.md } } },
        MuiDialogActions: {
          styleOverrides: {
            root: {
              borderTop: '1px solid',
              borderColor: effectiveDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
              backdropFilter: effectiveDark ? 'blur(16px)' : undefined,
              WebkitBackdropFilter: effectiveDark ? 'blur(16px)' : undefined,
              backgroundColor: effectiveDark ? 'rgba(30,30,46,0.85)' : undefined,
              zIndex: 1,
              padding: '8px 16px',
            },
          },
        },
        MuiDialogContent: {
          styleOverrides: {
            root: {
              overflowY: 'auto',
              flex: 1,
            },
          },
        },
        MuiChip: {
          defaultProps: {
            variant: 'filled',
          },
          styleOverrides: {
            root: { borderRadius: radii.pill, fontWeight: typography.fontWeight.bold },
            outlined: ({ ownerState, theme }) => ({
              border: 'none',
              backgroundColor: theme.palette[ownerState.color]?.main || (effectiveDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'),
              color: theme.palette[ownerState.color]?.contrastText || theme.palette.text.primary,
            }),
          },
        },
        MuiSwitch: {
          styleOverrides: {
            root: {
              width: 52,
              height: 26,
              padding: 0,
              marginRight: 12,
            },
            switchBase: {
              padding: 3,
              '&.Mui-checked': {
                transform: 'translateX(26px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                  backgroundColor: palette.main,
                  opacity: 1,
                  border: 0,
                },
              },
            },
            thumb: {
              width: 19,
              height: 19,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            },
            track: {
              borderRadius: 15,
              backgroundColor: effectiveDark ? 'rgba(255,255,255,0.2)' : '#e0e0e0',
              opacity: 1,
            },
            sizeSmall: {
              width: 42,
              height: 22,
              '& .MuiSwitch-switchBase': {
                padding: 3,
                '&.Mui-checked': {
                  transform: 'translateX(20px)',
                },
              },
              '& .MuiSwitch-thumb': {
                width: 16,
                height: 16,
              },
            },
          },
        },
        MuiFilledInput: {
          styleOverrides: {
            root: {
              borderRadius: `${radii.md}px ${radii.md}px 0 0`,
              backgroundColor: effectiveDark ? 'rgba(255,255,255,0.07)' : `${palette.main}0d`,
              '&:hover': {
                backgroundColor: effectiveDark ? 'rgba(255,255,255,0.10)' : `${palette.main}18`,
              },
              '&.Mui-focused': {
                backgroundColor: effectiveDark ? 'rgba(255,255,255,0.10)' : `${palette.main}18`,
              },
              '&:before': { borderBottomColor: effectiveDark ? 'rgba(255,255,255,0.2)' : `${palette.main}40` },
              '&:hover:not(.Mui-disabled):before': { borderBottomColor: palette.main },
              '&:after': { borderBottomColor: palette.main },
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: radii.md,
              backgroundColor: effectiveDark ? 'rgba(0,0,0,0.31)' : 'rgba(255,255,255,0.6)',
              backdropFilter: 'blur(2px)',
              transition: 'box-shadow 0.2s ease',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: `${palette.main}30`,
                borderWidth: '1px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: `${palette.main}70`,
              },
              '&.Mui-focused': {
                boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: palette.main,
                borderWidth: '2px',
              },
              '& input, & textarea': {
                fontWeight: 700,
              },
            },
          },
        },
        MuiDialog: {
          defaultProps: {
            keepMounted: true,
            transitionDuration: 0,
            slotProps: {
              backdrop: {
                sx: {
                  backgroundColor: effectiveDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.5)',
                },
              },
            },
          },
          styleOverrides: {
            paper: {
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              background: effectiveDark
                ? `linear-gradient(140deg, ${palette.main}1e 0%, rgba(20,20,35,0.88) 50%)`
                : `linear-gradient(140deg, ${palette.main}1a 0%, rgba(255,255,255,0.88) 70%)`,
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              willChange: 'backdrop-filter',
              transform: 'translateZ(0)',
              transition: 'opacity 200ms ease, transform 50ms ease',
              boxShadow: effectiveDark
                ? `0 24px 64px rgba(0,0,0,0.8), 0 0 0 1px ${palette.main}55`
                : `0 24px 64px rgba(0,0,0,0.12), 0 0 0 1px ${palette.main}44`,
            },
          },
        },
        MuiFormLabel: {
          styleOverrides: {
            root: {
              '&.Mui-focused': { color: palette.main },
            },
          },
        },
        MuiFab: {
          styleOverrides: {
            root: {
              boxShadow: palette.isSuper
                ? `0 4px 20px ${palette.effects?.glowColor || palette.main + '59'}`
                : `0 4px 16px ${palette.main}59`,
              '&:hover': {
                boxShadow: palette.isSuper
                  ? `0 6px 30px ${palette.effects?.glowColor || palette.main + '73'}`
                  : `0 6px 20px ${palette.main}73`,
              },
            },
          },
        },
      },
      breakpoints: {
        values: { xs: 0, sm: 600, md: 768, lg: 1024, xl: 1200 },
      },
    });
  }, [mode, themeTokens]);

  return theme;
};
